import type { RecoverySelection } from 'components/parking-agent/recoveryCsv'
/**
 * CSVStatusView - Download/parsing/error/success status UI
 */

import { LayerSelectionModal } from '../../LayerSelectionModal'
import { useFloorStore } from '../../../../../map-editor-shared/store/floorStore'
import { FileInfo } from '../../../../../models/FileStorage'
import { LayerInfo, SelectedLayerInfo } from '../../../../../services/DxfParserService'
import styles from '../../CSVUploader.module.css'
import BasicObjectSummary from '../../BasicObjectSummary'

interface CSVStatusViewProps {
  viewMode: string
  uploadState: any
  rawData: any
  parsedData: any
  hasObjectTypes: boolean
  isLoadingTypes: boolean
  downloadError: string | null
  downloadMessage: string
  showLayerModal: boolean
  selectedDxfFile: FileInfo | null
  analyzedLayers: LayerInfo[]
  siteProjectId: number | null
  isCadListDismissed: boolean
  onClear: () => void
  onRetry: () => void
  onSetViewMode: (mode: any) => void
  onLayerSelectionConfirm: (layers: SelectedLayerInfo[], recovery?: RecoverySelection) => Promise<void>
  onLayerSelectionCancel: () => void
}

export function CSVStatusView({
  viewMode,
  uploadState,
  rawData,
  parsedData,
  hasObjectTypes,
  isLoadingTypes,
  downloadError,
  downloadMessage,
  showLayerModal,
  selectedDxfFile,
  analyzedLayers,
  siteProjectId,
  isCadListDismissed,
  onClear,
  onRetry,
  onSetViewMode,
  onLayerSelectionConfirm,
  onLayerSelectionCancel,
}: CSVStatusViewProps) {
  const extractedTypes = (parsedData?.entities ?? []).map((entity: { layer: string }) => entity.layer)
  const isolationNotice =
    parsedData?.isolationReport?.removedCount > 0 ? (
      <p className={styles.statusDescription} role="status">
        멀리 떨어진 객체 {parsedData.isolationReport.removedCount}개 제외 · 필요하면 DXF 추출의 거리 제외 옵션을 끄고
        다시 변환하세요.
      </p>
    ) : null
  // Idle state - show dropzone or warning
  if (uploadState.status === 'idle' && viewMode === 'idle') {
    // CRITICAL FIX: If rawData exists, this is a restored floor with CSV data
    if (rawData !== null && rawData !== undefined) {
      const rowCount = parsedData?.rowCount || 0
      return (
        <div className={styles.container}>
          <div className={styles.statusCard}>
            <div className={styles.statusText}>
              <p className={styles.statusTitle}>데이터 로드 완료</p>
              <BasicObjectSummary types={extractedTypes} />
              {isolationNotice}
              <p className={styles.statusDescription}>층 데이터 - {rowCount}행</p>
            </div>
            <button onClick={onClear} className={styles.clearButton}>
              지우고 다시 업로드
            </button>
          </div>
        </div>
      )
    }

    // Show loading state while object types are being loaded from API
    if (isLoadingTypes) {
      return (
        <div className={styles.container}>
          <div className={styles.statusCard}>
            <div className={styles.spinner} />
            <div className={styles.statusText}>
              <p className={styles.statusTitle}>객체 타입 불러오는 중...</p>
              <p className={styles.statusDescription}>잠시만 기다려주세요</p>
            </div>
          </div>
        </div>
      )
    }

    if (!hasObjectTypes) {
      return (
        <div className={styles.container}>
          <div className={`${styles.statusCard} ${styles.warningCard}`}>
            <div className={styles.warningIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div className={styles.statusText}>
              <p className={styles.statusTitle}>객체 타입이 필요합니다</p>
              <p className={styles.statusDescription}>
                CSV 파일을 업로드하기 전에 먼저 객체 타입을 생성해주세요.
                <br />
                좌측 사이드바에서 객체 타입을 추가하거나 Import할 수 있습니다.
              </p>
            </div>
          </div>
        </div>
      )
    }

    // If current floor already has graph data, don't show file type selector
    const { currentFloor, floors } = useFloorStore.getState()
    const floorData = currentFloor ? floors.find((f) => f.id === currentFloor) : undefined
    if ((floorData?.mapData?.graphJson?.cells?.length ?? 0) > 0) {
      return null
    }

    if (isCadListDismissed) {
      return (
        <div className={styles.container}>
          <div className={styles.statusCard}>
            <div className={styles.statusText}>
              <p className={styles.statusTitle}>이 층은 아직 도면이 없습니다</p>
            </div>
            <button onClick={() => onSetViewMode('server-cad')} className={styles.clearButton}>
              서버 DXF 파일 선택
            </button>
          </div>
        </div>
      )
    }

    // CSV 업로드 선택은 제거 — 서버의 DXF 파일 목록을 바로 보여준다.
    onSetViewMode('server-cad')
    return null
  }

  // Downloading state - show progress
  if (viewMode === 'downloading' || uploadState.status === 'uploading') {
    const progress = uploadState.status === 'uploading' ? (uploadState as any).progress || 0 : 0
    return (
      <div className={styles.container}>
        <div className={styles.statusCard}>
          <div className={styles.spinner} />
          <div className={styles.statusText}>
            <p className={styles.statusTitle}>{downloadMessage}</p>
            <p className={styles.statusDescription}>잠시만 기다려주세요</p>
          </div>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>
    )
  }

  // Parsing state - show spinner
  if (viewMode === 'parsing' || uploadState.status === 'parsing') {
    return (
      <div className={styles.container}>
        <div className={styles.statusCard}>
          <div className={styles.spinner} />
          <div className={styles.statusText}>
            <p className={styles.statusTitle}>CSV 데이터 분석 중...</p>
            <p className={styles.statusDescription}>잠시 시간이 걸릴 수 있습니다</p>
          </div>
        </div>
      </div>
    )
  }

  // Error state - show error message
  if (viewMode === 'error' || uploadState.status === 'error') {
    const errorMessage =
      downloadError || (uploadState.status === 'error' ? (uploadState as any).message : '오류가 발생했습니다')
    return (
      <div className={styles.container}>
        <div className={`${styles.statusCard} ${styles.errorCard}`}>
          <div className={styles.errorIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className={styles.statusText}>
            <p className={styles.statusTitle}>작업 실패</p>
            <p className={styles.statusDescription}>{errorMessage}</p>
          </div>
          <button onClick={onRetry} className={styles.retryButton}>
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  // Parsed state - show success message or restored data message
  if (viewMode === 'parsed' || uploadState.status === 'parsed') {
    if (uploadState.status !== 'parsed') {
      return null
    }

    const parsedState = uploadState as any

    // For restored data, show a simpler status without the success animation
    if (parsedState.isRestored) {
      return (
        <div className={styles.container}>
          <div className={styles.statusCard}>
            <div className={styles.statusText}>
              <p className={styles.statusTitle}>데이터 로드 완료</p>
              <BasicObjectSummary types={extractedTypes} />
              {isolationNotice}
              <p className={styles.statusDescription}>
                {parsedState.fileName} - {parsedState.rowCount}행
              </p>
            </div>
            <button onClick={onClear} className={styles.clearButton}>
              지우고 다시 업로드
            </button>
          </div>
        </div>
      )
    }

    // For freshly uploaded data, show success message
    return (
      <div className={styles.container}>
        <div className={`${styles.statusCard} ${styles.successCard}`}>
          <div className={styles.successIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="9 12 12 15 16 10" />
            </svg>
          </div>
          <div className={styles.statusText}>
            <p className={styles.statusTitle}>로드 완료</p>
            <BasicObjectSummary types={extractedTypes} />
            {isolationNotice}
            <p className={styles.statusDescription}>
              {parsedState.fileName} - {parsedState.rowCount}행
            </p>
          </div>
          <button onClick={onClear} className={styles.clearButton}>
            지우고 다시 업로드
          </button>
        </div>
      </div>
    )
  }

  // Fallback: show layer modal if visible
  return (
    <>
      {showLayerModal && selectedDxfFile && (
        <LayerSelectionModal
          layers={analyzedLayers}
          isOpen={showLayerModal}
          onConfirm={onLayerSelectionConfirm}
          onCancel={onLayerSelectionCancel}
          filename={selectedDxfFile.original_name || selectedDxfFile.filename}
          sourceFilename={selectedDxfFile.filename}
          siteProjectId={siteProjectId}
        />
      )}
    </>
  )
}
